/**
 * In-document find — walk the block tree for plain-text matches and reveal
 * the current hit in the live DOM (scroll + text highlight). Does not mutate
 * content.
 */

/* global Highlight */

import { flattenBlocks } from './blockUtils';
import { htmlToText } from './caretUtils';

const CONTENT_TYPES = new Set([
  'text',
  'heading',
  'checkbox',
  'bullet',
  'number',
  'toggle',
  'choice',
  'choice-path',
  'code',
  'file-upload',
]);

const HIGHLIGHT_ALL = 'doc-find-all';
const HIGHLIGHT_CURRENT = 'doc-find-current';
const OVERLAY_ROOT_ID = 'doc-find-overlay-root';

/** Collect every searchable text segment in document order. */
export const collectSearchSegments = (blocks) => {
  const segments = [];
  for (const block of flattenBlocks(blocks || [])) {
    if (CONTENT_TYPES.has(block.type)) {
      const text = htmlToText(block.content || '');
      if (text) {
        segments.push({
          blockId: block.id,
          kind: block.type === 'code' ? 'code' : 'editable',
          text,
        });
      }
    }
    if (block.type === 'table') {
      const data = block.props?.tableData;
      if (!data) continue;
      const pushCell = (cell, rowIndex, colIndex) => {
        if (Array.isArray(cell)) {
          for (const inner of flattenBlocks(cell)) {
            if (!CONTENT_TYPES.has(inner.type)) continue;
            const text = htmlToText(inner.content || '');
            if (!text) continue;
            segments.push({
              blockId: inner.id,
              kind: inner.type === 'code' ? 'code' : 'editable',
              text,
            });
          }
          return;
        }
        const text = htmlToText(cell || '');
        if (!text) return;
        segments.push({
          blockId: block.id,
          kind: 'table',
          rowIndex,
          colIndex,
          text,
        });
      };
      (data.headers || []).forEach((cell, colIndex) => pushCell(cell, -1, colIndex));
      (data.rows || []).forEach((row, rowIndex) => {
        (row || []).forEach((cell, colIndex) => pushCell(cell, rowIndex, colIndex));
      });
    }
  }
  return segments;
};

/** All case-insensitive substring matches across the document. */
export const findMatches = (blocks, query) => {
  const q = (query || '').trim();
  if (!q) return [];
  const needle = q.toLowerCase();
  const matches = [];
  for (const seg of collectSearchSegments(blocks)) {
    const hay = seg.text.toLowerCase();
    let from = 0;
    while (from <= hay.length - needle.length) {
      const idx = hay.indexOf(needle, from);
      if (idx === -1) break;
      matches.push({
        blockId: seg.blockId,
        kind: seg.kind,
        rowIndex: seg.rowIndex,
        colIndex: seg.colIndex,
        start: idx,
        length: needle.length,
      });
      from = idx + Math.max(1, needle.length);
    }
  }
  return matches;
};

/** Ids from root → target (inclusive), or null. */
export const findBlockPathIds = (blocks, targetId) => {
  for (const block of blocks || []) {
    if (block.id === targetId) return [block.id];
    if (block.children?.length) {
      const childPath = findBlockPathIds(block.children, targetId);
      if (childPath) return [block.id, ...childPath];
    }
  }
  return null;
};

/** Open any collapsed toggles on the path to targetId. Returns next tree. */
export const withOpenTogglesTo = (blocks, targetId) => {
  const pathIds = findBlockPathIds(blocks, targetId);
  if (!pathIds?.length) return blocks;
  const path = new Set(pathIds);

  let changed = false;
  const walk = (list) =>
    list.map((block) => {
      let next = block;
      if (
        path.has(block.id) &&
        block.type === 'toggle' &&
        block.props?.isOpen === false
      ) {
        changed = true;
        next = { ...block, props: { ...block.props, isOpen: true } };
      }
      if (path.has(block.id) && block.type === 'choice') {
        const pathChild = (block.children || []).find(
          (child) => path.has(child.id) && child.type === 'choice-path'
        );
        if (pathChild && (block.props?.showAll || block.props?.activePathId !== pathChild.id)) {
          changed = true;
          next = {
            ...block,
            props: { ...block.props, showAll: false, activePathId: pathChild.id },
          };
        }
      }
      if (next.children?.length && path.has(block.id)) {
        const children = walk(next.children);
        if (children !== next.children) {
          next = { ...next, children };
        }
      }
      return next;
    });

  const next = walk(blocks || []);
  return changed ? next : blocks;
};

const findTextRange = (el, start, length) => {
  if (!el || length <= 0) return null;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let startNode = null;
  let startOffset = 0;
  let endNode = null;
  let endOffset = 0;
  let node = walker.nextNode();
  while (node) {
    const len = node.textContent.length;
    if (!startNode && start < offset + len) {
      startNode = node;
      startOffset = start - offset;
    }
    if (start + length <= offset + len) {
      endNode = node;
      endOffset = start + length - offset;
      break;
    }
    offset += len;
    node = walker.nextNode();
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  } catch {
    return null;
  }
};

const resolveMatchElement = (match) => {
  const wrapper = document.querySelector(`[data-block-id="${match.blockId}"]`);
  if (!wrapper) return null;
  if (match.kind === 'code') {
    return wrapper.querySelector('.code-block-editable, .code-block-textarea');
  }
  if (match.kind === 'table') {
    return wrapper.querySelector(
      `[data-table-cell="${match.blockId}-${match.rowIndex}-${match.colIndex}"]`
    );
  }
  return wrapper.querySelector('.doc-editable');
};

const supportsCssHighlight = () =>
  typeof CSS !== 'undefined' &&
  CSS.highlights &&
  typeof Highlight !== 'undefined';

const clearCssHighlights = () => {
  if (!supportsCssHighlight()) return;
  CSS.highlights.delete(HIGHLIGHT_ALL);
  CSS.highlights.delete(HIGHLIGHT_CURRENT);
};

const clearOverlayHighlights = () => {
  document.getElementById(OVERLAY_ROOT_ID)?.remove();
};

const ensureOverlayRoot = () => {
  let root = document.getElementById(OVERLAY_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = OVERLAY_ROOT_ID;
    root.className = 'doc-find-overlay-root';
    root.setAttribute('aria-hidden', 'true');
    document.body.appendChild(root);
  }
  return root;
};

/** Mirror-based rects for a substring inside a textarea/input. */
const textareaMatchRects = (el, start, length) => {
  const style = window.getComputedStyle(el);
  const mirror = document.createElement('div');
  const props = [
    'boxSizing',
    'width',
    'height',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'wordSpacing',
    'textIndent',
    'padding',
    'border',
    'lineHeight',
    'whiteSpace',
    'wordWrap',
    'overflowWrap',
  ];
  props.forEach((p) => {
    mirror.style[p] = style[p];
  });
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = style.whiteSpace || 'pre-wrap';
  mirror.style.overflow = 'auto';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';

  const value = el.value || '';
  const before = value.slice(0, start);
  const mid = value.slice(start, start + length);
  const mark = document.createElement('span');
  mark.textContent = mid || ' ';
  mirror.textContent = '';
  mirror.append(document.createTextNode(before), mark);
  document.body.appendChild(mirror);
  mirror.scrollTop = el.scrollTop;
  mirror.scrollLeft = el.scrollLeft;

  const elRect = el.getBoundingClientRect();
  const markRect = mark.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  document.body.removeChild(mirror);

  return [
    {
      left: elRect.left + (markRect.left - mirrorRect.left) - el.scrollLeft,
      top: elRect.top + (markRect.top - mirrorRect.top) - el.scrollTop,
      width: Math.max(markRect.width, 2),
      height: markRect.height || parseFloat(style.lineHeight) || 16,
    },
  ];
};

const paintOverlayRects = (rects, className) => {
  const root = ensureOverlayRoot();
  for (const rect of rects) {
    if (!rect || rect.width <= 0 || rect.height <= 0) continue;
    const node = document.createElement('div');
    node.className = className;
    node.style.left = `${rect.left}px`;
    node.style.top = `${rect.top}px`;
    node.style.width = `${rect.width}px`;
    node.style.height = `${rect.height}px`;
    root.appendChild(node);
  }
};

const rangeToRects = (range) => {
  const out = [];
  const list = range.getClientRects();
  for (let i = 0; i < list.length; i += 1) {
    const r = list[i];
    out.push({
      left: r.left,
      top: r.top,
      width: r.width,
      height: r.height,
    });
  }
  return out;
};

const matchToRange = (match) => {
  const el = resolveMatchElement(match);
  if (!el) return { el: null, range: null };
  if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
    return { el, range: null };
  }
  return { el, range: findTextRange(el, match.start, match.length) };
};

/** Nearest overflow-y scroll ancestor (or null). */
const scrollParentOf = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Center the actual match text in the scrollport (not the whole block).
 * Offsets for the floating find bar + sticky top shade so the hit lands
 * in the readable middle instead of slightly high/low.
 */
const scrollMatchIntoView = (match, resolved) => {
  const { el, range } = resolved;
  if (!el) return;

  let matchTop;
  let matchBottom;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
    const rects = textareaMatchRects(el, match.start, match.length);
    if (!rects.length) {
      const r = el.getBoundingClientRect();
      matchTop = r.top;
      matchBottom = r.bottom;
    } else {
      matchTop = Math.min(...rects.map((r) => r.top));
      matchBottom = Math.max(...rects.map((r) => r.top + r.height));
    }
  } else if (range) {
    const rects = range.getClientRects();
    if (rects.length) {
      matchTop = rects[0].top;
      matchBottom = rects[rects.length - 1].bottom;
    } else {
      const r = range.getBoundingClientRect();
      matchTop = r.top;
      matchBottom = r.bottom;
    }
  } else {
    const r = el.getBoundingClientRect();
    matchTop = r.top;
    matchBottom = r.bottom;
  }

  const scroller = scrollParentOf(el);
  if (!scroller) {
    // Fall back — no nested scroller; let the browser handle window scroll
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return;
  }

  const scrollerRect = scroller.getBoundingClientRect();
  const findBar = document.querySelector('.doc-find-bar');
  const findBarBottom = findBar
    ? findBar.getBoundingClientRect().bottom + 8
    : scrollerRect.top;
  // Readable band starts below the find bar (and sticky shade), ends at
  // the scrollport bottom. Center the match inside that band.
  const bandTop = Math.max(scrollerRect.top + 28, findBarBottom);
  const bandBottom = scrollerRect.bottom;
  const bandMid = (bandTop + bandBottom) / 2;
  const matchMid = (matchTop + matchBottom) / 2;
  const delta = matchMid - bandMid;
  if (Math.abs(delta) < 2) return;

  const max = scroller.scrollHeight - scroller.clientHeight;
  const next = Math.max(0, Math.min(max, scroller.scrollTop + delta));
  if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({ top: next, behavior: 'smooth' });
  } else {
    scroller.scrollTop = next;
  }
};

/**
 * Paint text-level highlights for all matches + emphasize the active one.
 * Keeps focus on `retainFocusEl` (the find input) when provided.
 */
export const revealFindMatch = (
  match,
  {
    attempt = 0,
    retainFocusEl = null,
    allMatches = null,
    activeIndex = 0,
    scrollToMatch = true,
  } = {}
) => {
  if (!match && !(allMatches && allMatches.length)) {
    clearFindHighlight();
    return;
  }

  const list = allMatches?.length ? allMatches : match ? [match] : [];
  const current = list[activeIndex] || match || list[0];
  if (!current) {
    clearFindHighlight();
    return;
  }

  const wrapper = document.querySelector(`[data-block-id="${current.blockId}"]`);
  const currentResolved = matchToRange(current);
  if (!wrapper || !currentResolved.el) {
    if (attempt < 8) {
      setTimeout(
        () =>
          revealFindMatch(match, {
            attempt: attempt + 1,
            retainFocusEl,
            allMatches,
            activeIndex,
            scrollToMatch,
          }),
        40
      );
    }
    return;
  }

  // Focus first (pinning any Chromium nudge) so it can't cancel the
  // smooth scroll that follows.
  if (retainFocusEl && document.activeElement !== retainFocusEl) {
    const scroller = scrollParentOf(wrapper);
    const pinned = scroller ? scroller.scrollTop : null;
    retainFocusEl.focus({ preventScroll: true });
    if (scroller != null && pinned != null && scroller.scrollTop !== pinned) {
      scroller.scrollTop = pinned;
    }
  }

  if (scrollToMatch) {
    scrollMatchIntoView(current, currentResolved);
  }

  clearCssHighlights();
  clearOverlayHighlights();

  const useCss = supportsCssHighlight();
  const allRanges = [];
  let currentRange = null;

  list.forEach((m, i) => {
    const { el, range } = matchToRange(m);
    if (!el) return;
    if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
      const rects = textareaMatchRects(el, m.start, m.length);
      paintOverlayRects(
        rects,
        i === activeIndex
          ? 'doc-find-overlay doc-find-overlay--current'
          : 'doc-find-overlay'
      );
      return;
    }
    if (!range) return;
    if (useCss) {
      allRanges.push(range);
      if (i === activeIndex) currentRange = range;
    } else {
      paintOverlayRects(
        rangeToRects(range),
        i === activeIndex
          ? 'doc-find-overlay doc-find-overlay--current'
          : 'doc-find-overlay'
      );
    }
  });

  if (useCss && allRanges.length) {
    CSS.highlights.set(HIGHLIGHT_ALL, new Highlight(...allRanges));
    if (currentRange) {
      CSS.highlights.set(HIGHLIGHT_CURRENT, new Highlight(currentRange));
    }
  }
};

export const clearFindHighlight = () => {
  clearCssHighlights();
  clearOverlayHighlights();
  document
    .querySelectorAll('.block-wrapper--find-active')
    .forEach((el) => el.classList.remove('block-wrapper--find-active'));
};